import { LayoutGrid, CalendarClock, Zap, CalendarDays, PieChart, Receipt, ListChecks } from "lucide-react";

import { HR_ROLES, EXPENSE_ANALYTICS_ROLES } from "@hrms/types";
import type { DashboardWidgetDef } from "../widget-registry.js";
import { KpiSummaryWidget } from "./KpiSummaryWidget.js";
import { PendingLeaveWidget } from "./PendingLeaveWidget.js";
import { QuickActionsWidget } from "./QuickActionsWidget.js";
import { AttendanceWidget } from "./AttendanceWidget.js";
import { MyTodosWidget } from "./MyTodosWidget.js";
import { EmployeeReportWidget } from "./EmployeeReportWidget.js";
import { ExpenseReportWidget } from "./ExpenseReportWidget.js";

/**
 * Every widget the dashboard can show.
 *
 * Adding one is a single entry here — the grid, the layout store, the edit UI and
 * the API all work off this contract and need no change (open-closed principle).
 *
 * `key` is a storage key: renaming one orphans every employee's saved row for
 * that widget, which silently resets it to its default. Treat keys as permanent.
 *
 * The order and sizes here are what someone sees BEFORE they customise anything:
 * the three full-width reports, then their own attendance at a third width. They
 * do not touch anyone who has already arranged their dashboard — a saved row wins
 * over every default below.
 */
export const DASHBOARD_WIDGETS: DashboardWidgetDef[] = [
  {
    key:          "kpi-summary",
    title:        "Summary",
    icon:         LayoutGrid,
    description:  "Headline counts: headcount, approvals, tickets, expiring documents.",
    component:    KpiSummaryWidget,
    defaultSize:  "full",
    defaultOrder: 1,
    // The seven tiles need the width; a narrow span would stack them awkwardly.
    allowedSizes: ["lg", "full"],
    isAvailable:  ({ role }) => HR_ROLES.has(role),
  },
  {
    key:          "employee-report",
    title:        "Employee report",
    icon:         PieChart,
    description:  "Headcount by status, department and office, as on the Employees page.",
    component:    EmployeeReportWidget,
    defaultSize:  "full",
    defaultOrder: 2,
    // The three chart panels need the width; below a two-thirds span they stack
    // into a column tall enough to push everything else off the fold.
    allowedSizes: ["lg", "full"],
    isAvailable:  ({ role }) => HR_ROLES.has(role),
  },
  {
    key:          "expense-report",
    title:        "Expense report",
    icon:         Receipt,
    description:  "Spend by period, category, status and billable split.",
    component:    ExpenseReportWidget,
    defaultSize:  "full",
    defaultOrder: 3,
    // The KPI row and the paired chart panels need the width; narrower than a
    // two-thirds span they stack into a very tall column.
    allowedSizes: ["lg", "full"],
    // The same roles the Expenses page gates its Report tab with.
    isAvailable:  ({ role }) => EXPENSE_ANALYTICS_ROLES.has(role),
  },
  {
    key:          "attendance",
    title:        "Attendance",
    icon:         CalendarDays,
    description:  "Your own present / absent / leave days, by week or month.",
    component:    AttendanceWidget,
    defaultSize:  "sm",
    defaultOrder: 4,
    // Small and Medium only. The calendar is a fixed seven columns, so past a
    // half-width card the day tiles just stretch without showing anything more;
    // the layout degrades downward through container queries instead.
    allowedSizes: ["sm", "md"],
    // Everyone has attendance, so this one is not role-gated.
  },
  {
    key:          "my-todos",
    title:        "My Todos",
    icon:         ListChecks,
    description:  "Your todos for the week or month.",
    component:    MyTodosWidget,
    defaultSize:  "sm",
    defaultOrder: 4.5,
    // Same card as Attendance, so the same sizes: a fixed seven-column strip gains
    // nothing past half width.
    allowedSizes: ["sm", "md"],
    // Everyone has their own todos, so this one is not role-gated.
  },
  {
    key:          "pending-leave",
    title:        "Pending leave requests",
    icon:         CalendarClock,
    description:  "Leave requests awaiting your decision.",
    component:    PendingLeaveWidget,
    defaultSize:  "md",
    defaultOrder: 5,
    isAvailable:  ({ role }) => HR_ROLES.has(role),
    // Off by default since the reports arrived: the dashboard opens on the three
    // full-width reports, and this queue is one click away in Leave. Anyone who
    // wants it back adds it from Customise.
    defaultHidden: true,
  },
  {
    key:          "quick-actions",
    title:        "Quick actions",
    icon:         Zap,
    description:  "Shortcuts to leave, timesheets, attendance, expenses and IT tickets.",
    component:    QuickActionsWidget,
    defaultSize:  "md",
    defaultOrder: 6,
    // Off by default for the same reason: every shortcut it holds is a sidebar
    // click away, so it earns its place only for someone who asks for it.
    defaultHidden: true,
  },
];
